import React from 'react';
import { makeStyles } from '@material-ui/core/styles';
import PropTypes from 'prop-types';
import clsx from 'clsx';

import Button from 'Mui/Button';
import MuiDialog from '@material-ui/core/Dialog';
import DialogTitle from '@material-ui/core/DialogTitle';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import IconButton from '@material-ui/core/IconButton';
import CloseIcon from '@material-ui/icons/Close';
import Typography from '@material-ui/core/Typography';

import { system900 } from 'Mui/styles/color';
import { dp08 } from 'Mui/styles/shadow';

const useStyles = makeStyles({
    dialog: {
        width: '100%',
        maxWidth: '320px',
        borderRadius: '16px',
        boxShadow: dp08.boxShadow,
        position: 'relative'
    },
    title: {
        padding: '12px 24px',
        whiteSpace: 'pre-wrap'
    },
    emptyTitle: {
        padding: '12px 24px',
        height: '48px'
    },
    content: {
        '&[class^="MuiDialogContent-root"]': {
            padding: '0',
            position: 'relative'
        }
    },
    closeIcon: {
        width: '24px',
        height: '24px',
        position: 'absolute',
        top: '12px',
        right: '12px',
        cursor: 'pointer',
        color: system900.color,
        'z-index': 2
    },
    footer: {
        display: 'flex',
        justifyContent: 'center',
        padding: '16px',
        flexDirection: 'column'
    },
    button: {
        borderRadius: '20px',
        minWidth: '125px',
        '&.MuiButtonBase-root': {
            margin: '0'
        },
        '&:not(:first-child)': {
            marginTop: '8px'
        }
    },
    largeButton: {
        minWidth: '182px',
        borderRadius: '24px',
        padding: '12px 8px'
    }
});

function Popup({
    isOpen = true,
    showClose,
    maxWidth = 'xs',
    onCancel,
    onClose,
    onConfirm,
    children,
    title,
    titleAlign = 'center',
    preserveTitleSpace = false,
    disableBackdropClick,
    disableEscapeKeyDown,
    startButton,
    startButtonVariant,
    startButtonColor,
    startButtonStyle,
    startButtonDisable,
    endButton,
    endButtonVariant = 'contained',
    endButtonColor = 'primary',
    endButtonStyle,
    endButtonDisable,
    endButtonSize = 'medium',
    className,
    backdropStyle = {},
    closeIconStyle
}) {
    const classes = useStyles();

    const handleClose = (_event, reason) => {
        if (disableBackdropClick && reason === 'backdropClick') {
            return false;
        }

        if (disableEscapeKeyDown && reason === 'escapeKeyDown') {
            return false;
        }

        if (typeof onClose === 'function') {
            onClose();
        }
    };

    return (
        <MuiDialog
            disableAutoFocus
            disableEnforceFocus
            disableRestoreFocus
            onClose={handleClose}
            open={isOpen}
            maxWidth={maxWidth}
            classes={{
                paper: `${classes.dialog} ${className}`
            }}
            BackdropProps={{
                style: {
                    backgroundColor: 'rgba(255, 255, 255, 0.56)',
                    backdropFilter: 'blur(4px)',
                    ...backdropStyle
                }
            }}
        >
            {showClose ? (
                <IconButton onClick={onClose} size="small" className={classes.closeIcon}>
                    <CloseIcon className={closeIconStyle} />
                </IconButton>
            ) : null}
            {(Boolean(title) || preserveTitleSpace) && (
                <DialogTitle
                    disableTypography
                    className={title ? classes.title : classes.emptyTitle}
                >
                    {title && (
                        <Typography align={titleAlign} variant="h6">
                            {title}
                        </Typography>
                    )}
                </DialogTitle>
            )}
            <DialogContent classes={{ root: classes.content }}>{children}</DialogContent>
            {(startButton || endButton) && (
                <DialogActions className={classes.footer}>
                    {Boolean(endButton) && (
                        <Button
                            variant={endButtonVariant}
                            color={endButtonColor}
                            onClick={onConfirm}
                            disabled={endButtonDisable}
                            className={clsx(
                                classes.button,
                                endButtonStyle,
                                (startButton || endButtonSize === 'large') && classes.largeButton
                            )}
                        >
                            {endButton}
                        </Button>
                    )}
                    {Boolean(startButton) && (
                        <Button
                            onClick={onCancel || onClose}
                            variant={startButtonVariant}
                            color={startButtonColor}
                            disabled={startButtonDisable}
                            className={clsx(
                                classes.button,
                                startButtonStyle,
                                endButton && classes.largeButton
                            )}
                        >
                            {startButton}
                        </Button>
                    )}
                </DialogActions>
            )}
        </MuiDialog>
    );
}

Popup.propTypes = {
    isOpen: PropTypes.bool,
    showClose: PropTypes.bool,
    maxWidth: PropTypes.string,
    onCancel: PropTypes.func,
    onClose: PropTypes.func,
    onConfirm: PropTypes.func,
    children: PropTypes.node,
    title: PropTypes.string,
    titleAlign: PropTypes.string,
    preserveTitleSpace: PropTypes.bool,
    disableBackdropClick: PropTypes.bool,
    disableEscapeKeyDown: PropTypes.bool,
    startButton: PropTypes.string,
    startButtonVariant: PropTypes.string,
    startButtonColor: PropTypes.string,
    startButtonStyle: PropTypes.string,
    startButtonDisable: PropTypes.bool,
    endButton: PropTypes.string,
    endButtonVariant: PropTypes.string,
    endButtonColor: PropTypes.string,
    endButtonStyle: PropTypes.string,
    endButtonDisable: PropTypes.bool,
    endButtonSize: PropTypes.string,
    className: PropTypes.string,
    backdropStyle: PropTypes.object
};

export default Popup;
